import { io, type Socket } from 'socket.io-client';
import { AuthService } from './AuthService.js';
import { GameStateManager } from '../state/GameStateManager.js';

declare const __APP_VERSION__: string;

function getSocketUrl(): string {
  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) return envUrl;
  if (typeof window !== 'undefined' && window.location?.hostname !== 'localhost') return '';
  return 'http://localhost:4000';
}

/**
 * Manages a Socket.IO connection to the game server.
 * Connects when the user is authenticated and listens for admin kick events.
 */
export class SocketService {
  private static instance: SocketService;
  private socket: Socket | null = null;
  private onForceLogout: ((reason?: string) => void) | null = null;
  private onForceUpdate: (() => void) | null = null;
  /** Listeners fired on every successful socket `connect` event (initial AND
   *  every auto-reconnect). Used by services that hold per-connection
   *  server-side state (e.g. MultiplayerService.enterMenu's `mpMenuMode`)
   *  and need to restore it after a transient disconnect. */
  private reconnectListeners: Array<() => void> = [];

  private constructor() {}

  static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  /** Set the callback invoked when a force-logout event is received. Receives optional reason string. */
  setForceLogoutHandler(handler: (reason?: string) => void): void {
    this.onForceLogout = handler;
  }

  /** Set the callback invoked when the server broadcasts a force-update event. */
  setForceUpdateHandler(handler: () => void): void {
    this.onForceUpdate = handler;
  }

  /** Connect to the server with the current auth token. Idempotent. */
  connect(): void {
    if (this.socket?.connected) return;
    const token = AuthService.getInstance().getToken();
    if (!token) return;

    this.socket = io(getSocketUrl(), {
      auth: { token, version: __APP_VERSION__ },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 5000,
    });

    // Fire reconnect listeners on every successful connect (initial + every
    // auto-reconnect). The server's per-socket state (e.g. mpMenuMode) is
    // wiped on disconnect; services that depend on it must re-emit their
    // session state so subsequent server-side gates (invite delivery, queue
    // membership) keep working after a transient disconnect.
    this.socket.on('connect', () => {
      for (const fn of this.reconnectListeners) {
        try { fn(); } catch (e) { console.warn('[socket] reconnect listener threw', e); }
      }
    });

    this.socket.on('force-logout', (data?: { reason?: string }) => {
      const auth = AuthService.getInstance();
      auth.logout();
      this.disconnect();
      this.onForceLogout?.(data?.reason);
    });

    this.socket.on('force-update', () => {
      this.onForceUpdate?.();
    });

    this.socket.on('resources-updated', (data: { gold?: number; loot?: number }) => {
      const gsm = GameStateManager.getInstance();
      if (typeof data.gold === 'number') gsm.player.resources.gold = data.gold;
      if (typeof data.loot === 'number') gsm.player.resources.loot = data.loot;
      gsm.save();
    });

    this.socket.on('connect_error', () => {
      // Silent — server may be unreachable, ServerHealthService handles that
    });
  }

  /** Expose the raw socket for multiplayer event binding. */
  getSocket(): Socket | null {
    return this.socket;
  }

  /** Register a callback to fire on every socket `connect` event (initial
   *  connect AND every auto-reconnect). Idempotent — re-adding the same fn
   *  is a no-op. Callers use this to re-emit session-restoration events
   *  (e.g. `multiplayer:enter-menu`) so server-side per-connection state
   *  survives transient disconnects. */
  addReconnectListener(fn: () => void): void {
    if (this.reconnectListeners.includes(fn)) return;
    this.reconnectListeners.push(fn);
  }

  /** Disconnect the socket. Called on logout. */
  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }
}
